import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material'
import { primary } from '../../theme/palette'
import AddIcon from '@mui/icons-material/Add'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined'
import LinkIcon from '@mui/icons-material/Link'
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined'
import HubOutlinedIcon from '@mui/icons-material/HubOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import { motion } from 'framer-motion'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@cmms/api-client'
import type { Asset, AssetCategory } from '@cmms/types'

import { useCopyLink } from '../../hooks/useCopyLink'
import { useConfirm } from '../../components/ConfirmDialog'
import { CustomerScopePicker } from '../../components/CustomerScopePicker'
import { CustomerScopeChip } from '../../components/CustomerScopeChip'
import { useOrgSettings } from '../../hooks/useOrgSettings'
import { usePermissions } from '../../hooks/usePermissions'
import { PageShell } from '../../layouts/PageShell'
import { api } from '../../lib/api'
import { toast } from '../../store/snackbarStore'
import { ICON_REGISTRY, getCategoryIcon, useCategoryIconStore } from './iconRegistry'

// Color presets for the picker
const COLOR_PRESETS = [
  '#0D9488', '#0284C7', '#D97706', '#059669', '#DC2626',
  '#7C3AED', '#EA580C', '#2563EB', '#475569', '#0E7490',
  '#DB2777', '#65A30D',
]

export function CategoriesPage() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { categoryId: urlCategoryId } = useParams<{ categoryId?: string }>()
  const copyLink = useCopyLink()
  const confirmDlg = useConfirm()
  const { formatDate } = useOrgSettings()
  const { can } = usePermissions()

  const categoriesQ = useQuery({ queryKey: ['asset-categories'], queryFn: () => api.assets.listCategories() })
  const categories: AssetCategory[] = Array.isArray(categoriesQ.data) ? categoriesQ.data : []

  const [selectedId, setSelectedId] = useState<string | null>(urlCategoryId ?? null)
  useEffect(() => { if (urlCategoryId) setSelectedId(urlCategoryId) }, [urlCategoryId])
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<AssetCategory | null>(null)

  // Auto-select first
  if (!selectedId && categories.length > 0) {
    const first = categories[0]!.id
    setSelectedId(first)
    navigate(`/categories/${first}`, { replace: true })
  }
  function handleSelectCategory(id: string) {
    setSelectedId(id)
    navigate(`/categories/${id}`, { replace: true })
  }

  const selected = selectedId ? categories.find((c) => c.id === selectedId) ?? null : null
  const totalAssets = categories.reduce((s, c) => s + (c.asset_count ?? 0), 0)

  const catAssetsQ = useQuery({
    queryKey: ['assets', { category_id: selected?.id }],
    queryFn: () => api.assets.list({ category_id: selected!.id }),
    enabled: !!selected,
    staleTime: 30_000,
  })
  const catAssets: Asset[] = Array.isArray(catAssetsQ.data) ? catAssetsQ.data : []

  const deleteM = useMutation({
    mutationFn: (id: string) => api.assets.remove(id), // This should be asset-categories delete — let me check
    onSuccess: () => { toast.success('Category deleted'); qc.invalidateQueries({ queryKey: ['asset-categories'] }) },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Failed to delete'),
  })

  // The API client doesn't have a dedicated deleteCategory method on assets.
  // Asset categories have their own endpoint. Let me use a direct call.
  async function handleDelete(cat: AssetCategory) {
    const ok = await confirmDlg({
      title: `Delete "${cat.name}"?`,
      message: cat.asset_count > 0
        ? `This category has ${cat.asset_count} asset${cat.asset_count === 1 ? '' : 's'}. Remove them from this category first.`
        : 'This category will be permanently removed.',
      confirmLabel: 'Delete',
      variant: 'danger',
    })
    if (!ok) return
    try {
      await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/v1/asset-categories/${cat.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${(await import('../../store/authStore')).useAuthStore.getState().accessToken}` },
      })
      toast.success('Category deleted')
      qc.invalidateQueries({ queryKey: ['asset-categories'] })
      if (selectedId === cat.id) setSelectedId(null)
    } catch {
      toast.error('Failed to delete — may have assets attached.')
    }
  }

  return (
    <>
      <PageShell title="Categories" subtitle="Group assets by type so you can filter and report faster." scrollContent={false}
        actions={
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}>
            <Tooltip title={can('categories:create') ? '' : "You don't have permission"} arrow>
              <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="contained" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={() => setCreateOpen(true)} disabled={!can('categories:create')}
                sx={{ fontSize: 13, fontWeight: 600, borderRadius: 2, boxShadow: primary.glow }}>
                New category
              </Button></Box>
            </Tooltip>
          </motion.div>
        }>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: { xs: 'column', md: 'row' },
          border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper', mb: { xs: 3, md: 4 } }}>

          {/* List */}
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, width: { xs: '100%', md: 360 }, flexShrink: 0,
            borderRight: { md: '1px solid' }, borderColor: 'divider' }}>
            <Stack direction="row" alignItems="center" sx={{ px: 3, py: 1.75, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', fontWeight: 500 }}>
                {categories.length} categor{categories.length === 1 ? 'y' : 'ies'} · {totalAssets} assets
              </Typography>
            </Stack>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1,
              scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
              '&::-webkit-scrollbar': { width: 6 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
              '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
              {categoriesQ.isLoading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
              {categories.map((c, i) => {
                const sel = c.id === selectedId
                const color = c.color ?? '#64748B'
                const CIcon = getCategoryIcon(c.id, c.name)
                return (
                  <motion.div key={c.id} initial={{ opacity: 0, y: 3 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(0.4, i * 0.015), duration: 0.24, ease: [0.32, 0.72, 0, 1] }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} onClick={() => handleSelectCategory(c.id)}
                      sx={{
                        position: 'relative', px: 2.5, py: 1.5, cursor: 'pointer',
                        transition: 'background-color 120ms ease',
                        bgcolor: sel ? primary.selected : 'transparent',
                        '&:hover': { bgcolor: sel ? primary.selectedHover : (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.035)' },
                        '&::before': sel ? { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, bgcolor: 'primary.main' } : undefined,
                      }}>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: `${color}18`, color, flexShrink: 0 }}>
                        <CIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: sel ? 600 : 500 }} noWrap>{c.name}</Typography>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.15 }}>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {c.asset_count ?? 0} asset{(c.asset_count ?? 0) === 1 ? '' : 's'}
                          </Typography>
                          <CustomerScopeChip customerId={c.customer_id} />
                        </Stack>
                      </Box>
                    </Stack>
                  </motion.div>
                )
              })}
            </Box>
          </Box>

          {/* Detail */}
          {selected ? (
            <motion.div key={selected.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.26 }}
              style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
              <Box sx={{ px: 4, pt: 4, pb: 3, flexShrink: 0 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2.5}>
                  {(() => { const SIcon = getCategoryIcon(selected.id, selected.name); return (
                  <Box sx={{ width: 52, height: 52, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${selected.color ?? '#64748B'}18`, color: selected.color ?? '#64748B', flexShrink: 0 }}>
                    <SIcon sx={{ fontSize: 26 }} />
                  </Box>
                  ) })()}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.2 }}>{selected.name}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: selected.color ?? '#64748B' }} />
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        {selected.asset_count ?? 0} asset{(selected.asset_count ?? 0) === 1 ? '' : 's'}
                      </Typography>
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Tooltip title="Copy link" arrow>
                      <IconButton size="small" onClick={() => copyLink('/categories')}
                        sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34 }}>
                        <LinkIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={can('categories:update') ? '' : "You don't have permission"} arrow>
                      <Box component="span" sx={{ display: 'inline-flex' }}><Button variant="outlined" size="small" startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                        onClick={() => setEditTarget(selected)} disabled={!can('categories:update')}
                        sx={{ fontSize: 12.5, fontWeight: 600, color: 'primary.main', borderColor: 'divider', height: 34 }}>Edit</Button></Box>
                    </Tooltip>
                    <Tooltip title={can('categories:delete') ? 'Delete category' : "You don't have permission"} arrow>
                      <Box component="span" sx={{ display: 'inline-flex' }}><IconButton size="small" onClick={() => handleDelete(selected)} disabled={!can('categories:delete')}
                        sx={{ color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 1.25, width: 34, height: 34, '&:hover': { color: 'error.main' } }}>
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton></Box>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 4, py: 3 }}>
                {selected.description && (
                  <><Sec title="Description"><Typography sx={{ fontSize: 14, lineHeight: 1.65 }}>{selected.description}</Typography></Sec><Divider /></>
                )}
                <Sec title="Color">
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: `${selected.color ?? '#64748B'}18`, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: selected.color ?? '#64748B' }} />
                    </Box>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 500, fontFamily: 'ui-monospace, monospace' }}>{selected.color ?? 'None'}</Typography>
                  </Stack>
                </Sec>
                <Divider />
                <Sec title={`Assets (${catAssets.length})`}>
                  {catAssets.length === 0 ? (
                    <Box sx={{ py: 3, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5 }}>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No assets assigned to this category yet.</Typography>
                    </Box>
                  ) : (
                    <Stack spacing={0}>
                      {catAssets.map((a) => (
                        <Stack key={a.id} direction="row" alignItems="center" spacing={1.5}
                          onClick={() => navigate(`/assets/${a.id}`)}
                          sx={{
                            px: 1.25, py: 1.25, borderRadius: 1.25, cursor: 'pointer',
                            transition: 'background-color 0.15s',
                            '&:hover': { bgcolor: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)' },
                          }}>
                          <Box sx={{
                            width: 30, height: 30, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            bgcolor: `${selected.color ?? '#64748B'}18`, color: selected.color ?? '#64748B',
                          }}>
                            <HubOutlinedIcon sx={{ fontSize: 15 }} />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{a.name}</Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                              <Typography sx={{ fontSize: 10.5, fontFamily: 'ui-monospace, monospace', color: 'text.secondary' }}>{a.asset_code}</Typography>
                              {a.site_name && (
                                <>
                                  <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>·</Typography>
                                  <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{a.site_name}</Typography>
                                </>
                              )}
                            </Stack>
                          </Box>
                          <OpenInNewOutlinedIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Sec>
                <Divider />
                <Sec title="Metadata">
                  <Stack spacing={1.25}>
                    <Kv label="Created" value={formatDate(selected.created_at)} />
                  </Stack>
                </Sec>
              </Box>
            </motion.div>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Select a category</Typography>
            </Box>
          )}
        </Box>
      </PageShell>

      {createOpen && <CategoryFormDialog mode="create" onClose={() => setCreateOpen(false)} />}
      {editTarget && <CategoryFormDialog mode="edit" category={editTarget} onClose={() => setEditTarget(null)} />}
    </>
  )
}

// ─── Create / Edit dialog ───────────────────────────────────────────────────

function CategoryFormDialog({ mode, category, onClose }: {
  mode: 'create' | 'edit'; category?: AssetCategory; onClose: () => void
}) {
  const qc = useQueryClient()
  const setIconStore = useCategoryIconStore((s) => s.setIcon)
  const currentIconKey = useCategoryIconStore((s) => category ? s.icons[category.id] : undefined)
  const [name, setName] = useState(category?.name ?? '')
  const [desc, setDesc] = useState(category?.description ?? '')
  const [color, setColor] = useState(category?.color ?? '#0D9488')
  const [iconKey, setIconKey] = useState(currentIconKey ?? 'category')
  const [customerId, setCustomerId] = useState<string | null>((category as any)?.customer_id ?? null)

  async function handleSubmit() {
    const base = import.meta.env.VITE_API_BASE_URL || ''
    const token = (await import('../../store/authStore')).useAuthStore.getState().accessToken
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers['Authorization'] = `Bearer ${token}`
    // Also propagate the active customer context so the server can
    // double-check; this matches what the api-client interceptor does
    // for the axios paths.
    const activeCustomer = (await import('../../store/customerStore')).useCustomerStore.getState().customerId
    if (activeCustomer) headers['X-Customer-Id'] = activeCustomer

    try {
      const url = mode === 'create'
        ? `${base}/api/v1/asset-categories`
        : `${base}/api/v1/asset-categories/${category!.id}`
      const res = await fetch(url, {
        method: mode === 'create' ? 'POST' : 'PATCH',
        headers,
        body: JSON.stringify({
          name: name.trim(), description: desc.trim() || undefined, color,
          customer_id: customerId,
        }),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        throw new Error((data as any).message ?? 'Request failed')
      }
      const data = await res.json().catch(() => null)
      // Save icon choice to local store
      const catId = mode === 'edit' ? category!.id : (data as any)?.id
      if (catId && iconKey) setIconStore(catId, iconKey)
      toast.success(mode === 'create' ? 'Category created' : 'Category updated')
      qc.invalidateQueries({ queryKey: ['asset-categories'] })
      onClose()
    } catch (e: any) {
      toast.error(e.message ?? 'Failed')
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>
        {mode === 'create' ? 'New category' : `Edit ${category?.name}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} size="small" fullWidth autoFocus placeholder="e.g. HVAC, Plumbing" />
          <TextField label="Description (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} size="small" fullWidth multiline minRows={2} />

          <CustomerScopePicker
            mode="catalog"
            value={customerId}
            onChange={setCustomerId}
            helperText="All = available to every customer. Pick one to scope this category."
          />

          {/* Color picker */}
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Color</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {COLOR_PRESETS.map((c) => (
                <Box key={c} onClick={() => setColor(c)}
                  sx={{
                    width: 32, height: 32, borderRadius: '50%', cursor: 'pointer',
                    border: '3px solid', borderColor: color === c ? c : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'border-color 120ms ease, transform 120ms ease',
                    '&:hover': { transform: 'scale(1.1)' },
                  }}>
                  <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: c }} />
                </Box>
              ))}
            </Stack>
          </Box>

          {/* Icon picker */}
          <Box>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'text.secondary', mb: 1 }}>Icon</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 0.75, maxHeight: 180, overflowY: 'auto',
              scrollbarWidth: 'thin', scrollbarColor: 'transparent transparent',
              '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: 99 },
              '&:hover::-webkit-scrollbar-thumb': { background: (t) => t.palette.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)' } }}>
              {ICON_REGISTRY.map((entry) => {
                const active = iconKey === entry.key
                const IC = entry.Component
                return (
                  <Tooltip key={entry.key} title={entry.label} arrow>
                    <Box onClick={() => setIconKey(entry.key)}
                      sx={{
                        aspectRatio: '1 / 1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 1.25, cursor: 'pointer',
                        border: '1.5px solid', borderColor: active ? color : 'transparent',
                        bgcolor: active ? `${color}14` : 'transparent',
                        color: active ? color : 'text.secondary',
                        transition: 'all 120ms ease',
                        '&:hover': { bgcolor: `${color}0A`, color },
                      }}>
                      <IC sx={{ fontSize: 20 }} />
                    </Box>
                  </Tooltip>
                )
              })}
            </Box>
          </Box>

          {/* Preview */}
          {(() => { const PIcon = ICON_REGISTRY.find((e) => e.key === iconKey)?.Component ?? CategoryOutlinedIcon; return (
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2, py: 1.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: `${color}18`, color, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <PIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{name || 'Preview'}</Typography>
          </Stack>
          ) })()}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!name.trim()}>
          {mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Sec({ title, children }: { title: string; children: React.ReactNode }) {
  return <Box sx={{ py: 2.5 }}><Typography sx={{ fontSize: 13.5, fontWeight: 600, mb: 1.25 }}>{title}</Typography>{children}</Box>
}

function Kv({ label, value }: { label: string; value: string }) {
  return <Stack direction="row" alignItems="flex-start" spacing={1.5}>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary', minWidth: 80 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 500 }}>{value}</Typography>
  </Stack>
}
